import { useCallback, useState } from 'react';

import { isDefined } from '@klareospace/pharos-lib-utils';

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const STORAGE_KEY_PREFIX = 'pharos.table-page-size.';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UsePersistedPageSizeConfig {
  /** Page size used when no valid value is stored (default: 25). Falls back to `options[0]` if not itself in `options`. */
  defaultPageSize?: number;
  /** Selectable page-size values (default: [10, 25, 50, 100]). */
  options?: number[];
}

export interface UsePersistedPageSizeReturn {
  options: number[];
  pageSize: number;
  setPageSize: (pageSize: number) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function readStoredPageSize(storageKey: string, options: number[], defaultPageSize: number): number {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!isDefined(raw)) {
      return defaultPageSize;
    }
    const parsed = Number(raw);
    return options.includes(parsed) ? parsed : defaultPageSize;
  } catch {
    return defaultPageSize;
  }
}

function writeStoredPageSize(storageKey: string, pageSize: number): void {
  try {
    window.localStorage.setItem(storageKey, String(pageSize));
  } catch {
    // Ignore storage failures (e.g. private browsing quota, storage disabled)
  }
}

function resolveEffectiveDefaultPageSize(options: number[], defaultPageSize: number): number {
  if (options.length === 0 || options.includes(defaultPageSize)) {
    return defaultPageSize;
  }
  return options[0];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/** Persists a table's page size to localStorage under a per-table key; falls back to `defaultPageSize` on any error or invalid stored value. */
export function usePersistedPageSize(
  storageKey: string,
  config: UsePersistedPageSizeConfig = {},
): UsePersistedPageSizeReturn {
  const { defaultPageSize = DEFAULT_PAGE_SIZE, options = DEFAULT_PAGE_SIZE_OPTIONS } = config;
  const fullStorageKey = `${STORAGE_KEY_PREFIX}${storageKey}`;
  const effectiveDefaultPageSize = resolveEffectiveDefaultPageSize(options, defaultPageSize);

  const [pageSize, setPageSizeState] = useState<number>(() =>
    readStoredPageSize(fullStorageKey, options, effectiveDefaultPageSize),
  );

  const setPageSize = useCallback(
    (nextPageSize: number): void => {
      setPageSizeState(nextPageSize);
      writeStoredPageSize(fullStorageKey, nextPageSize);
    },
    [fullStorageKey],
  );

  return { options, pageSize, setPageSize };
}
